import { ChevronLeft, ShieldCheck, Sparkles } from "lucide-react";
import { useNavigate } from "react-router-dom";

const UPDATED_DATE = "12 August 2026";

function PolicySection({ number, title, children }) {
  return (
    <section className="scroll-mt-28">
      <h2 className="flex items-start gap-3 text-xl font-semibold tracking-tight text-slate-950 sm:text-2xl">
        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary-800 text-xs font-bold text-white">
          {number}
        </span>
        {title}
      </h2>

      <div className="mt-4 space-y-4 pl-0 text-sm leading-7 text-slate-600 sm:pl-10 sm:text-[15px]">
        {children}
      </div>
    </section>
  );
}

export default function LoyaltyTermsPage() {
  const navigate = useNavigate();

  return (
    <main className="min-h-screen bg-[#fcfaf9] pb-16">
      <section className="border-b border-[#eadde1] bg-white">
        <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 text-sm font-semibold text-slate-600 transition hover:text-primary-800"
          >
            <ChevronLeft size={17} />
            Back
          </button>

          <div className="mt-8 grid gap-8 lg:grid-cols-[1fr_auto] lg:items-end">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full bg-primary-50 px-3 py-1.5 text-xs font-bold uppercase tracking-[0.14em] text-primary-800">
                <Sparkles size={14} />
                CHARMS Rewards
              </div>

              <h1 className="mt-4 text-4xl font-semibold tracking-tight text-slate-950 sm:text-5xl">
                Loyalty Programme
                <span className="block text-primary-800">
                  Terms & Conditions
                </span>
              </h1>

              <p className="mt-4 max-w-2xl text-sm leading-6 text-slate-600 sm:text-base">
                Everything you need to know about earning, redeeming and
                managing your CHARMS loyalty points.
              </p>
            </div>

            <div className="rounded-2xl border border-[#eadde1] bg-[#faf7f5] px-5 py-4 text-sm text-slate-600">
              <p className="font-semibold text-slate-900">Last updated</p>
              <p className="mt-1">{UPDATED_DATE}</p>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
        <div className="grid gap-8 lg:grid-cols-[220px_minmax(0,1fr)]">
          <aside className="h-fit rounded-2xl border border-[#eadde1] bg-white p-5 lg:sticky lg:top-48">
            <p className="text-xs font-bold uppercase tracking-[0.16em] text-primary-800">
              Quick summary
            </p>

            <div className="mt-4 space-y-3 text-sm text-slate-600">
              <div className="rounded-xl bg-[#faf7f5] p-3">
                <p className="font-semibold text-slate-950">1 Point = ₹1</p>
                <p className="mt-1 text-xs leading-5">
                  Redeem points at eligible online and POS checkouts.
                </p>
              </div>

              <div className="rounded-xl bg-[#faf7f5] p-3">
                <p className="font-semibold text-slate-950">Earn up to 10%</p>
                <p className="mt-1 text-xs leading-5">
                  Based on the final eligible payable amount.
                </p>
              </div>

              <div className="rounded-xl bg-[#faf7f5] p-3">
                <p className="font-semibold text-slate-950">FIFO expiry</p>
                <p className="mt-1 text-xs leading-5">
                  Points expire after 6 months, oldest points first.
                </p>
              </div>
            </div>
          </aside>

          <article className="rounded-3xl border border-[#eadde1] bg-white p-6 shadow-sm sm:p-10">
            <div className="mb-10 rounded-2xl border border-primary-100 bg-primary-50 p-5">
              <div className="flex gap-3">
                <ShieldCheck
                  className="mt-0.5 shrink-0 text-primary-800"
                  size={21}
                />

                <p className="text-sm leading-6 text-primary-950">
                  These Loyalty Programme Terms and Conditions govern the CHARMS
                  Loyalty Programme. The Programme is offered by Charms
                  Collections Pvt. Ltd. (“CHARMS”, “we”, “us” or “our”) and is
                  available through eligible CHARMS stores, the CHARMS website
                  and related customer applications.
                </p>
              </div>
            </div>

            <div className="space-y-10">
              <PolicySection number="1" title="Eligibility and Account">
                <p>
                  The Programme is available to eligible customers with a CHARMS
                  customer account or a customer profile linked to a verified
                  mobile number or email address.
                </p>

                <p>
                  You must provide accurate information and keep access to your
                  account secure. CHARMS may refuse, suspend or close Programme
                  access where there is suspected fraud, misuse, duplicate
                  accounts or breach of these terms.
                </p>
              </PolicySection>

              <PolicySection number="2" title="Points Value">
                <p>
                  One (1) loyalty point has a redemption value of ₹1 when
                  redeemed through an eligible CHARMS checkout or point-of-sale
                  transaction.
                </p>

                <p>
                  Points are not cash, cannot be transferred, sold, pledged or
                  exchanged for money, and have no value outside the Programme
                  except where applicable law requires otherwise.
                </p>
              </PolicySection>

              <PolicySection number="3" title="Earning Points">
                <p>
                  Unless CHARMS displays a different eligible offer, you may
                  earn loyalty points of up to 10% of the final payable value of
                  an eligible completed purchase after applicable discounts,
                  coupons and loyalty-point redemptions.
                </p>

                <p>
                  For example, if an eligible bill is ₹4,500 and ₹1,000 points
                  are redeemed, the final payable value is ₹3,500 and
                  approximately 350 points may be earned.
                </p>

                <p>
                  Points are credited only after a sale or online order is
                  successfully completed in our records. Pending, cancelled,
                  failed, unpaid, fraudulent, reversed, returned or refunded
                  transactions do not earn points. CHARMS may correct points
                  credited in error.
                </p>
              </PolicySection>

              <PolicySection number="4" title="Redeeming Points">
                <p>
                  You may redeem available points during an eligible online
                  checkout or at an eligible CHARMS POS checkout. The checkout
                  screen will show the points available and the permitted
                  redemption amount.
                </p>

                <p>
                  You must select or identify the linked customer account before
                  redemption. Points cannot be redeemed beyond your available
                  balance or the eligible bill amount.
                </p>
              </PolicySection>

              <PolicySection number="5" title="Returns, Refunds and Exchanges">
                <p>
                  If an item is returned, refunded, cancelled or exchanged,
                  CHARMS may reverse the points originally earned for the
                  returned portion. If points were used to pay for the returned
                  portion, CHARMS may restore the corresponding eligible points
                  rather than issue cash for the points-funded amount, subject
                  to our Returns, Refunds and Exchanges Policy and applicable
                  law.
                </p>

                <p>
                  For an exchange settled in one transaction, the applicable
                  return value and new-sale value will be calculated together.
                  Any difference may be payable by the customer or returned
                  through an eligible approved method. Points earned on the
                  exchange are based on the final net payable value after the
                  exchange settlement.
                </p>
              </PolicySection>

              <PolicySection number="6" title="Expiry, Limits and Changes">
                <p>
                  Loyalty points expire after six (6) months unless otherwise
                  communicated. Expiry is applied on a first-in, first-out
                  (FIFO) basis, meaning your oldest available points are
                  redeemed or expire first.
                </p>

                <p>
                  We may set transaction limits, eligible-product exclusions,
                  minimum or maximum redemption rules, and promotional terms
                  from time to time. Such terms will be displayed in the
                  relevant checkout, offer or Programme communication.
                </p>
              </PolicySection>

              <PolicySection
                number="7"
                title="Suspension, Adjustment and Termination"
              >
                <p>
                  CHARMS may suspend, deduct, adjust or cancel points and
                  Programme access where we reasonably believe there is fraud,
                  abuse, unauthorised access, incorrect crediting, a chargeback,
                  a returned purchase or a breach of these terms.
                </p>

                <p>
                  We may modify or discontinue the Programme on reasonable
                  notice, except where immediate action is needed for security,
                  fraud prevention or legal compliance.
                </p>
              </PolicySection>

              <PolicySection number="8" title="Privacy">
                <p>
                  We process Programme information in accordance with the CHARMS
                  Privacy Policy. By participating, you understand that we use
                  purchase, return and redemption information to administer the
                  Programme, prevent misuse and provide relevant customer
                  support and offers where permitted.
                </p>
              </PolicySection>

              <PolicySection number="9" title="Contact and Grievances">
                <p>
                  For loyalty-related questions, account issues, point disputes
                  or grievances, please contact CHARMS Customer Support.
                </p>

                <div className="rounded-2xl bg-[#faf7f5] p-5 text-sm">
                  <p>
                    <strong className="text-slate-950">
                      Customer Support:
                    </strong>{" "}
                    support@charmscollections.in
                  </p>

                  <p className="mt-2">
                    <strong className="text-slate-950">Website:</strong>{" "}
                    charmscollections.in
                  </p>

                  <p className="mt-2 text-slate-500">
                    For formal grievances, contact the grievance officer details
                    displayed in the CHARMS Privacy Policy.
                  </p>
                </div>
              </PolicySection>
            </div>
          </article>
        </div>
      </section>
    </main>
  );
}
